import React, { useEffect, useMemo, useState } from "react";
import { Form, Input, Button, Upload, message } from "antd";
import type { UploadChangeParam } from "antd/es/upload";
import { UploadOutlined } from "@ant-design/icons";
import { updateProfile } from "firebase/auth";
import { uploadBytes, getDownloadURL, ref } from "firebase/storage";
import { useLoaderData } from "react-router-dom";
import { auth, storageRef } from "~/lib/firebase";
import { apiClient } from "~/services/apiClient";
import { ReceiptSignaturePad } from "~/components/ReceiptSignaturePad";
import type { FirestoreUserProfile } from "~/services/server/user-tenant/getUserInfoWithLambda";
import { INTENTS } from "~/utils";
import type { AccountPageLoaderType } from "./loaders/AccountPageLoader";

type ProfileFormValues = {
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  organizationID: string;
};

function defaultAvatarUrl(username: string | undefined): string {
  const key = encodeURIComponent(username || "user");
  return `https://robohash.org/${key}?size=200x200&bgset=bg2`;
}

const AccountPage: React.FC = () => {
  const loaderData = useLoaderData() as AccountPageLoaderType;
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState<FirestoreUserProfile | null>(
    loaderData.profile,
  );
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [form] = Form.useForm<ProfileFormValues>();

  const initialValues = useMemo<ProfileFormValues>(
    () => ({
      email: profile?.email ?? loaderData.user.email ?? "",
      username: profile?.username ?? loaderData.user.username ?? "",
      firstName: profile?.firstName ?? loaderData.user.firstName ?? "",
      lastName: profile?.lastName ?? loaderData.user.lastName ?? "",
      organizationID: loaderData.user.organizationID ?? "",
    }),
    [profile, loaderData.user],
  );

  useEffect(() => {
    form.setFieldsValue(initialValues);
  }, [form, initialValues]);

  const avatarSrc =
    profile?.profilePictureUrl ?? defaultAvatarUrl(profile?.username);

  const handleImageChange = (info: UploadChangeParam) => {
    if (info.fileList.length > 0) {
      const file = info.fileList[0].originFileObj;
      if (file) setProfileImage(file);
    }
  };

  const handleUpdateProfile = async (values: ProfileFormValues) => {
    try {
      setLoading(true);
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("User not authenticated");

      let profilePictureUrl = profile?.profilePictureUrl;

      if (profileImage) {
        const fileRef = ref(
          storageRef,
          `userImages/${currentUser.uid}/profilePic.jpg`,
        );
        await uploadBytes(fileRef, profileImage);
        profilePictureUrl = await getDownloadURL(fileRef);
      }

      const result = await apiClient.sendIntent<{ profile: FirestoreUserProfile }>(
        INTENTS._update_user_profile,
        {
          firstName: values.firstName,
          lastName: values.lastName,
          ...(profilePictureUrl !== undefined ? { profilePictureUrl } : {}),
        },
      );

      if (!result.success || !result.data?.profile) {
        message.error(result.error ?? "Failed to update profile");
        return;
      }

      setProfile(result.data.profile);
      setProfileImage(null);

      if (profilePictureUrl) {
        await updateProfile(currentUser, { photoURL: profilePictureUrl });
      }

      message.success("Profile updated successfully");
    } catch (error) {
      console.error("Error updating profile:", error);
      message.error("Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h1>Account Details</h1>
      <img
        data-tour="account-avatar"
        src={avatarSrc}
        alt="Profile"
        style={{ width: 100, borderRadius: "50%" }}
      />
      <Form
        data-tour="account-profile-form"
        form={form}
        onFinish={handleUpdateProfile}
        layout="vertical"
      >
        <Form.Item name="email" label="Email Address">
          <Input disabled />
        </Form.Item>
        <Form.Item name="username" label="Username">
          <Input disabled />
        </Form.Item>
        <Form.Item name="firstName" label="First Name">
          <Input />
        </Form.Item>
        <Form.Item name="lastName" label="Last Name">
          <Input />
        </Form.Item>
        <Form.Item name="organizationID" label="Organization ID">
          <Input disabled />
        </Form.Item>
        <Form.Item label="Profile Picture">
          <Upload
            name="profileImage"
            listType="picture"
            maxCount={1}
            beforeUpload={() => false}
            onChange={handleImageChange}
          >
            <Button icon={<UploadOutlined />}>Upload Image</Button>
          </Upload>
        </Form.Item>
        <Form.Item>
          <Button
            data-tour="account-save"
            type="primary"
            htmlType="submit"
            loading={loading}
          >
            Update Profile
          </Button>
        </Form.Item>
      </Form>
      {auth.currentUser ? (
        <ReceiptSignaturePad
          initialSignatureUrl={loaderData.receiptSignatureUrl}
        />
      ) : null}
    </div>
  );
};

export default AccountPage;
